import React, { useState } from "react";
import { Card, Button, Divider, Switch, Tooltip } from "antd";
import { LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { featureFlagActions, FeatureFlagState, Flag, FlagSource } from "../../store/featureFlags";
import { actionCreators as uiActions } from "../../store/UI";
import { IApplicationState } from "../../store";
import { setReduxLogEnabled, isReduxLogEnabled } from "../../store/middleware/ReduxLogger";


const cornerPositions = {
    "top-left": { top: 0, left: 0 },
    "top-right": { top: 0, right: 0 },
    "bottom-left": { bottom: 0, left: 0 },
    "bottom-right": { bottom: 0, right: 0 },
} as const;

type Corner = keyof typeof cornerPositions;

const DebugPanel = () => {
    const dispatch = useDispatch();
    const [minimized, setMinimized] = useState(true);
    const [corner, setCorner] = useState<Corner>("bottom-right");
    const [isLocked, setIsLocked] = useState(true);
    const selectedSite = useSelector((state: IApplicationState) => state.sites.selectedSite);

    const [reduxLogging, setReduxLogging] = useState(isReduxLogEnabled());

    const {
        flags,
        loading,
        hasLoaded,
        abandoned,
        lastLoadedVenueId,
        userSessionId
    } = useSelector((state: { featureFlags: FeatureFlagState }) => state.featureFlags);

    const previewMode = useSelector((state: IApplicationState) => state.ui.previewMode);

    const cycleCorner = () => {
        const isSmallScreen = window.innerWidth < 768;

        if (isSmallScreen) {
            setCorner((prev) => (prev === "top-left" ? "bottom-left" : "top-left"));
        } else {
            const order: Corner[] = ["top-right", "top-left", "bottom-left", "bottom-right"];
            const nextIndex = (order.indexOf(corner) + 1) % order.length;
            setCorner(order[nextIndex]);
        }
    };


    const handleToggle = (key: string, value: boolean) => {
        if (!isLocked) {
            const newFlag = { enabled: value, venueId: selectedSite?.siteId, source: FlagSource.Override } as Flag;
            dispatch(featureFlagActions.setFlag(key, newFlag));
        }
    };

    const onTogglePreviewMode = (value: boolean) => {
        if (!isLocked) {
            dispatch(uiActions.setPreviewMode(value));
        }
    };

    const onToggleReduxLogging = (value: boolean) => {
        setReduxLogEnabled(value);
        setReduxLogging(value); // keep UI in sync
    };

    if (minimized) {
        return (
            <div
                onClick={() => setMinimized(false)}
                style={{
                    position: "fixed",
                    zIndex: 9999,
                    width: 50,
                    height: 20,
                    backgroundColor: "#ff0000",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: 8,
                    fontFamily: "monospace",
                    fontSize: 10,
                    color: "white",
                    userSelect: "none",
                    ...cornerPositions[corner],
                    margin: 8,
                }}
            >
                Debug
            </div>
        );
    }

    return (
        <Card
            size="small"
            title="Debug"
            extra={
                <div style={{ display: "flex", gap: 4 }}>
                    <Button size="small" onClick={() => setMinimized(true)} style={{ height: 24, fontSize: 12 }}>
                        Minimize
                    </Button>
                    <Button size="small" onClick={cycleCorner} style={{ height: 24, fontSize: 12 }}>
                        Move
                    </Button>
                </div>
            }
            style={{
                position: "fixed",
                zIndex: 9999,
                width: 400,
                maxHeight: "60vh",
                overflowY: "auto",
                fontFamily: "monospace",
                fontSize: "12px",
                ...cornerPositions[corner],
                margin: 8,
                backgroundColor: "#ffefef",
                border: '3px solid #ff0000'
            }}
            headStyle={{ borderBottom: '2px solid red', fontWeight: 'bold' }}
            bodyStyle={{ padding: "12px" }}
        >
            <div><strong>userSessionId:</strong> <br/>{String(userSessionId)}</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, width: 240 }}>
                <div>
                    <div><strong>loading:</strong> {String(loading)}</div>
                    <div><strong>hasLoaded:</strong> {String(hasLoaded)}</div>
                    <div><strong>abandoned:</strong> {String(abandoned)}</div>
                    <div><strong>lastLoadedVenue</strong> {String(lastLoadedVenueId)}</div>
                    <div><strong>SelectedVenue</strong> {String(selectedSite?.siteId ?? 'undefined')}</div>
                </div>
                <div>
                    <Tooltip title={isLocked ? "Unlock to edit" : "Lock editing"}>
                        <Button
                            size="large"
                            type={isLocked ? "default" : "primary"}
                            icon={isLocked ? <LockOutlined /> : <UnlockOutlined />}
                            onClick={() => setIsLocked(!isLocked)}
                        />
                    </Tooltip>
                </div>
            </div>

            <Divider style={{ borderColor: 'red' }} />

            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Contentful data preview Mode</span>
                <Switch
                    size="small"
                    checked={previewMode}
                    disabled={isLocked}
                    onChange={(checked) => onTogglePreviewMode(checked)}
                />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Log Redux events to browser console</span>
                <Switch
                    size="small"
                    checked={reduxLogging}
                    disabled={isLocked}
                    onChange={(checked) => onToggleReduxLogging(checked)}
                />
            </div>

            <Divider style={{ borderColor: 'red' }} />

            {Object.entries(flags).map(([key, value]) => (
                <div key={key} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{key}</span>
                    <span>{value.source}</span>
                    <span>{value.venueId}</span>
                    <Switch
                        size="small"
                        checked={value.enabled}
                        disabled={isLocked}
                        onChange={(checked) => handleToggle(key, checked)}
                    />
                </div>
            ))}
        </Card>
    );
};

export default DebugPanel;
